import React, { useState } from 'react';
import './Pricing.css';

const Pricing = () => {
  const [selectedPackage, setSelectedPackage] = useState('3-day');

  const packages = [
    {
      id: '2-day',
      name: '2-Day Adventure',
      duration: '2 Days, 1 Night',
      distance: '450+ Miles',
      price: 895,
      originalPrice: 995,
      savings: 100,
      description: 'Perfect for riders who want to experience the Devil\'s Highway on the weekend without the full 3-day commitment.',
      features: [
        'Professional guide',
        '1 night premium lodging',
        '1 group dinner + 1 breakfast',
        'Route briefings and curated stops',
        'Souvenir patch',
        '450+ total guided miles'
      ],
      itinerary: [
        'Day 1: Phoenix to Holbrook via Route 66',
        'Day 2: Devil\'s Highway and return to Phoenix'
      ],
      popular: false
    },
    {
      id: '3-day',
      name: '3-Day Ultimate',
      duration: '3 Days, 2 Nights',
      distance: '666+ Miles',
      price: 999,
      originalPrice: 1295,
      savings: 296,
      description: 'The complete Devil\'s Highway experience with full Route 66 integration and maximum riding time.',
      features: [
        'Professional guide',
        '2 nights premium lodging',
        '2 group dinners + 2 breakfasts',
        'Daily route briefings and curated stops',
        'Exclusive souvenir patch',
        '666+ total guided miles',
        'Route 66 to Route 666 experience'
      ],
      itinerary: [
        'Day 1: Phoenix to Holbrook via Route 66',
        'Day 2: The legendary Devil\'s Highway',
        'Day 3: Scenic return through New Mexico'
      ],
      popular: true
    }
  ];

  const addOns = [
    {
      name: 'Passenger',
      price: 395,
      description: 'Two-up on a motorcycle, sharing the rider\'s room. One passenger per bike. Includes their meals and activities.',
      icon: '👥',
      optional: true
    },
    {
      name: 'Photographer',
      price: 495,
      description: 'Optional moto photo journalist for the full tour. One photographer covers the whole group.',
      icon: '📷',
      optional: true
    },
    {
      name: 'GoPro Rental',
      price: 75,
      description: 'Optional action camera rental, priced per camera.',
      icon: '📹',
      optional: true
    },
    {
      name: 'Hat and t-shirt',
      price: 45,
      description: 'Optional merch, priced each. A souvenir patch is already included in every package.',
      icon: '🧢',
      optional: true
    }
  ];

  const selectedPackageData = packages.find(pkg => pkg.id === selectedPackage);

  return (
    <section id="pricing" className="pricing section">
      <div className="container">
        <div className="section-header">
          <h2 className="section-title">Tour Packages</h2>
          <p className="section-subtitle">
            Choose your Devil's Highway adventure. Photographer, GoPro, merch, and passenger pricing are optional extras — not part of the base package.
          </p>
        </div>

        <div className="package-selector">
          {packages.map((pkg) => (
            <button
              key={pkg.id}
              className={`package-tab ${selectedPackage === pkg.id ? 'active' : ''}`}
              onClick={() => setSelectedPackage(pkg.id)}
            >
              <span className="package-name">{pkg.name}</span>
              <span className="package-duration">{pkg.duration}</span>
            </button>
          ))}
        </div>

        <div className="pricing-content">
          <div className="package-details">
            <div className="package-header">
              <div className="package-badge">
                {selectedPackageData.popular && <span className="popular-badge">Most Popular</span>}
                <h3>{selectedPackageData.name}</h3>
                <p className="package-description">{selectedPackageData.description}</p>
              </div>
              
              <div className="package-pricing">
                <div className="price-container">
                  <span className="currency">$</span>
                  <span className="price">{selectedPackageData.price}</span>
                  <span className="price-period">per motorcycle</span>
                </div>
                <div className="price-details">
                  <span className="original-price">${selectedPackageData.originalPrice}</span>
                  <span className="savings">Save ${selectedPackageData.savings}</span>
                </div>
              </div>
            </div>

            <div className="package-info">
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-icon">⏱️</span>
                  <div>
                    <span className="info-label">Duration</span>
                    <span className="info-value">{selectedPackageData.duration}</span>
                  </div>
                </div>
                <div className="info-item">
                  <span className="info-icon">📏</span>
                  <div>
                    <span className="info-label">Distance</span>
                    <span className="info-value">{selectedPackageData.distance}</span>
                  </div>
                </div>
                <div className="info-item">
                  <span className="info-icon">🏍️</span>
                  <div>
                    <span className="info-label">Skill Level</span>
                    <span className="info-value">Advanced</span>
                  </div>
                </div>
                <div className="info-item">
                  <span className="info-icon">👥</span>
                  <div>
                    <span className="info-label">Group Size</span>
                    <span className="info-value">2-12 Riders</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="package-features">
              <h4>What's Included</h4>
              <ul className="features-list">
                {selectedPackageData.features.map((feature, index) => (
                  <li key={index} className="feature-item">
                    <span className="feature-check">✓</span>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="add-ons-section">
            <h3>Optional Extras</h3>
            <p className="add-ons-note">None of these are required. The tour price above already covers your guide, lodging, group meals, and patch.</p>
            <div className="add-ons-grid">
              {addOns.map((addon, index) => (
                <div key={index} className="add-on-card">
                  <div className="add-on-icon">{addon.icon}</div>
                  <div className="add-on-content">
                    <div className="add-on-heading">
                      <h4 className="add-on-name">{addon.name}</h4>
                      {addon.optional && <span className="optional-badge">Optional</span>}
                    </div>
                    <p className="add-on-description">{addon.description}</p>
                    <div className="add-on-price">+${addon.price}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="pricing-cta">
          <div className="cta-content">
            <h3>Ready to Ride the Devil's Highway?</h3>
            <p>Book your spot on America's most twisted road adventure</p>
            <a href="#booking" className="btn btn-primary">
              Book Now - ${selectedPackageData.price}
            </a>
          </div>
        </div>

        <div className="pricing-note">
          <div className="note-content">
            <span className="note-icon">ℹ️</span>
            <div>
              <h4>Booking Information</h4>
              <p>
                All prices are per motorcycle rider. Passenger pricing is additional. 
                A 50% deposit is required to secure your spot, with the balance due 30 days before departure. 
                Full refund available up to 60 days before tour date. Professional riding gear and a well-maintained 
                motorcycle are required for all participants.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
